import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { linkCasoPortal, subirArquivoCaso, removerArquivoCaso, urlAssinadaArquivoCaso } from '../lib/casoPortal';

const CATEGORIA_ARQUIVO_INFO = {
  contrato:   { label: 'Contrato assinado', icone: '📄' },
  comprovante:{ label: 'Comprovante',       icone: '🧾' },
};

const CHECKLIST_DOCS_PADRAO = [
  'RG e CPF', 'Comprovante de residência', 'Comprovante de renda',
  'Extratos bancários (últimos 6 meses)', 'Contratos de empréstimo/financiamento',
  'Procuração assinada', 'Declaração de hipossuficiência assinada',
];

const TITULOS_TIPO_PECA = {
  solicitacao_contrato: 'Solicitação de Cópia do Contrato',
  notificacao: 'Notificação Extrajudicial', consumidor_gov_bacen: 'Texto p/ consumidor.gov/BACEN',
  peticao_inicial: 'Petição Inicial', embargos_execucao: 'Embargos à Execução',
  embargos_monitoria: 'Embargos à Ação Monitória', excecao_pre_executividade: 'Exceção de Pré-Executividade',
  replica: 'Réplica', impugnacao_laudo: 'Impugnação ao Laudo Pericial', contrarrazoes: 'Contrarrazões',
  apelacao: 'Apelação', agravo_instrumento: 'Agravo de Instrumento', alegacoes_finais: 'Alegações Finais',
  exibicao_documentos: 'Exibição de Documentos', plano_pagamento: 'Plano de Pagamento', outra: 'Outra peça',
};
const STATUS_PECA_INFO = {
  rascunho: { label: 'Rascunho', bg: '#fef3c7', cor: '#92400e' },
  revisado: { label: 'Revisado', bg: '#dbeafe', cor: '#1d4ed8' },
  enviado:  { label: 'Enviado/Protocolado', bg: '#dcfce7', cor: '#16a34a' },
};

const STATUS_DOC_CICLO = { pendente: 'enviado', enviado: 'assinado', assinado: 'pendente' };
const STATUS_DOC_INFO = {
  pendente: { label: 'Pendente', bg: '#fef3c7', cor: '#92400e' },
  enviado:  { label: 'Enviado',  bg: '#dbeafe', cor: '#1d4ed8' },
  assinado: { label: 'Assinado', bg: '#dcfce7', cor: '#16a34a' },
};

const ETAPAS_CHECKLIST = [
  { campo: 'dt_contrato_assinado',            label: 'Contrato assinado' },
  { campo: 'dt_pagamento_realizado',          label: 'Pagamento realizado' },
  { campo: 'dt_procuracao_hipossuficiencia',  label: 'Procuração e hipossuficiência enviada' },
  { campo: 'dt_documentos_entregues',         label: 'Documentos entregues' },
  { campo: 'dt_notificacao_enviada',          label: 'Notificação enviada' },
  { campo: 'dt_acao_protocolada',             label: 'Ação protocolada' },
  { campo: 'dt_impugnacao',                   label: 'Impugnação' },
  { campo: 'dt_sentenca',                     label: 'Sentença' },
];

// Etapas de pagamento/procuração viraram marcações independentes (acima),
// porque na prática não seguem uma ordem fixa em relação ao andamento do
// caso. O quadro (kanban) fica só com a fase de atendimento interno.
const STATUS_COLUNAS = [
  'Contrato Assinado',
  'Passou pro Advogado',
];

const COR_COLUNA = {
  'Contrato Assinado':           { cor:'#7B2FBE', grad:'linear-gradient(135deg,#3F0051,#7B2FBE)' },
  'Passou pro Advogado':         { cor:'#4A90D9', grad:'linear-gradient(135deg,#1d4ed8,#60a5fa)' },
};

function statusDoCaso(caso) {
  return caso.status || 'Contrato Assinado';
}

function hoje() { return new Date().toISOString().split('T')[0]; }
function diasDesde(iso) { return iso ? Math.floor((new Date() - new Date(iso)) / 86400000) : null; }
function fmtDataHoraIso(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' })}`;
}
function diasAtraso(dataStr) {
  if (!dataStr) return 0;
  const hojeStr = new Date().toISOString().split('T')[0];
  return Math.max(0, Math.floor((new Date(hojeStr) - new Date(dataStr)) / 86400000));
}
function agora() {
  const d = new Date();
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' })}`;
}
function fmtMoeda(v) {
  return (Number(v)||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
}

const CASO_VAZIO = {
  projeto_id: '', nome_cliente: '', tipos_acao: [], link_drive: '', resumo: '', responsavel: '',
  status: 'Contrato Assinado',
  dt_contrato_assinado: null, dt_pagamento_realizado: null, dt_procuracao_hipossuficiencia: null,
  dt_documentos_entregues: null, dt_notificacao_enviada: null,
  dt_acao_protocolada: null, dt_impugnacao: null, dt_sentenca: null,
  etapas_extra: [], notas: [], parceria: false, parceiro_nome: '',
  honorarios_exito: null, honorarios_sucumbencia: null,
  pendente_preenchimento: false,
};

export default function Casos() {
  const [searchParams] = useSearchParams();
  const [casos, setCasos]                     = useState([]);
  const [cobrancas, setCobrancas]             = useState([]);
  const [projetos, setProjetos]               = useState([]);
  const [colaboradores, setColaboradores]     = useState([]);
  const [tiposAcaoDisponiveis, setTiposAcao]  = useState([]);
  const [carregando, setCarregando]           = useState(true);
  const [confirmDeleteCaso, setConfirmDeleteCaso] = useState(null);
  const [aba, setAba]                         = useState('casos');
  const [abaPainel, setAbaPainel]             = useState('andamento'); // andamento | valores | notas

  const [filtroProjeto, setFiltroProjeto]     = useState('');
  const [filtroTipoAcao, setFiltroTipoAcao]   = useState('');

  const [casoAberto, setCasoAberto]           = useState(null);
  const [modalCaso, setModalCaso]             = useState(false);
  const [casoEditando, setCasoEditando]       = useState(null);
  const [form, setForm]                       = useState(CASO_VAZIO);
  const [erroSalvar, setErroSalvar]           = useState('');
  const [salvando, setSalvando]               = useState(false);
  const [novaNota, setNovaNota]               = useState('');
  const [novaEtapaExtra, setNovaEtapaExtra]   = useState('');
  const [novaEtapaVisivelCliente, setNovaEtapaVisivelCliente] = useState(true);

  const [modalCobranca, setModalCobranca]     = useState(false);
  const [formCobranca, setFormCobranca]       = useState({ valor_total:'', forma_pagamento:'pix', n_parcelas:1, data_primeira:'', pct_nosso:30 });

  const [editandoCobrancaId, setEditandoCobrancaId] = useState(null);
  const [editCobranca, setEditCobranca]       = useState({ descricao:'', valor_total:'', data_prevista:'' });

  const [mesCalendario, setMesCalendario]     = useState(() => { const d=new Date(); return { ano:d.getFullYear(), mes:d.getMonth() }; });

  const [documentos, setDocumentos]           = useState([]);
  const [novoDocumento, setNovoDocumento]     = useState('');
  const [linkCopiado, setLinkCopiado]         = useState(false);

  const [arquivos, setArquivos]               = useState([]);
  const [categoriaNovoArquivo, setCategoriaNovoArquivo] = useState('contrato');
  const [nomeNovoArquivo, setNomeNovoArquivo] = useState('');
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);
  const [erroArquivo, setErroArquivo]         = useState('');

  const [atualizacoes, setAtualizacoes]       = useState([]);
  const [novaAtualizacaoTexto, setNovaAtualizacaoTexto] = useState('');
  const [enviandoAtualizacao, setEnviandoAtualizacao] = useState(false);

  const [pecas, setPecas]                     = useState([]);

  useEffect(() => { carregarTudo(); }, []);

  useEffect(() => {
    if (!casoAberto) { setDocumentos([]); setArquivos([]); setAtualizacoes([]); setPecas([]); return; }
    supabase.from('caso_documentos').select('*').eq('caso_id', casoAberto.id).order('ordem').order('created_at')
      .then(({ data }) => setDocumentos(data || []));
    supabase.from('caso_arquivos').select('*').eq('caso_id', casoAberto.id).order('created_at', { ascending:false })
      .then(({ data }) => setArquivos(data || []));
    supabase.from('caso_atualizacoes').select('*').eq('caso_id', casoAberto.id).order('created_at', { ascending:false })
      .then(({ data }) => setAtualizacoes(data || []));
    supabase.from('caso_pecas').select('*').eq('caso_id', casoAberto.id).order('created_at', { ascending:false })
      .then(({ data }) => setPecas(data || []));
  }, [casoAberto?.id]);

  async function adicionarAtualizacao(caso) {
    if (!novaAtualizacaoTexto.trim()) return;
    setEnviandoAtualizacao(true);
    const { data, error } = await supabase.from('caso_atualizacoes')
      .insert([{ caso_id: caso.id, texto: novaAtualizacaoTexto.trim() }]).select().single();
    if (!error && data) { setAtualizacoes([data, ...atualizacoes]); setNovaAtualizacaoTexto(''); }
    setEnviandoAtualizacao(false);
  }

  async function removerAtualizacao(atualizacao) {
    if (!window.confirm('Remover essa atualização? O cliente deixa de vê-la.')) return;
    const { error } = await supabase.from('caso_atualizacoes').delete().eq('id', atualizacao.id);
    if (!error) setAtualizacoes(atualizacoes.filter(a => a.id !== atualizacao.id));
  }

  async function handleUploadArquivo(caso, file) {
    if (!file) return;
    const nome = nomeNovoArquivo.trim() || file.name;
    setEnviandoArquivo(true); setErroArquivo('');
    try {
      const novo = await subirArquivoCaso(caso.id, categoriaNovoArquivo, nome, file);
      setArquivos([novo, ...arquivos]);
      setNomeNovoArquivo('');
    } catch (e) {
      setErroArquivo('Não consegui enviar: ' + e.message);
    }
    setEnviandoArquivo(false);
  }

  async function handleAbrirArquivo(arquivo) {
    try {
      const url = await urlAssinadaArquivoCaso(arquivo.storage_path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert('Não consegui abrir o arquivo: ' + e.message);
    }
  }

  async function handleRemoverArquivo(arquivo) {
    if (!window.confirm(`Remover "${arquivo.nome}"? O cliente deixa de ver esse arquivo.`)) return;
    try {
      await removerArquivoCaso(arquivo);
      setArquivos(arquivos.filter(a => a.id !== arquivo.id));
    } catch (e) {
      alert('Não consegui remover: ' + e.message);
    }
  }

  async function copiarLinkCliente(caso) {
    if (!caso.cliente_token) return;
    await navigator.clipboard.writeText(linkCasoPortal(caso.cliente_token));
    setLinkCopiado(true);
    setTimeout(() => setLinkCopiado(false), 1500);
  }

  async function usarChecklistPadrao(caso) {
    const jaTem = new Set(documentos.map(d => d.nome));
    const novos = CHECKLIST_DOCS_PADRAO.filter(n => !jaTem.has(n)).map((nome, i) => ({ caso_id: caso.id, nome, ordem: documentos.length + i }));
    if (novos.length === 0) return;
    const { data } = await supabase.from('caso_documentos').insert(novos).select();
    if (data) setDocumentos([...documentos, ...data]);
  }

  async function adicionarDocumento(caso) {
    if (!novoDocumento.trim()) return;
    const { data, error } = await supabase.from('caso_documentos').insert([{ caso_id: caso.id, nome: novoDocumento.trim(), ordem: documentos.length }]).select().single();
    if (!error && data) setDocumentos([...documentos, data]);
    setNovoDocumento('');
  }

  async function ciclarStatusDocumento(doc) {
    const status = STATUS_DOC_CICLO[doc.status] || 'pendente';
    const { data, error } = await supabase.from('caso_documentos').update({ status, atualizado_em: new Date().toISOString() }).eq('id', doc.id).select().single();
    if (!error && data) setDocumentos(documentos.map(d => d.id === data.id ? data : d));
  }

  async function removerDocumento(doc) {
    const { error } = await supabase.from('caso_documentos').delete().eq('id', doc.id);
    if (!error) setDocumentos(documentos.filter(d => d.id !== doc.id));
  }

  async function carregarTudo() {
    setCarregando(true);
    const [{ data: casosData }, { data: cobrancasData }, { data: projetosData }, { data: colabData }, { data: configData }] = await Promise.all([
      supabase.from('casos').select('*').order('created_at', { ascending:false }),
      supabase.from('cobrancas_casos').select('*').order('data_prevista'),
      supabase.from('projetos').select('id, nome, cor').order('nome'),
      supabase.from('colaboradores').select('nome').ilike('status','ativo').order('nome'),
      supabase.from('configuracoes').select('*').eq('chave','tipos_acao').maybeSingle(),
    ]);
    setCasos(casosData || []);
    setCobrancas(cobrancasData || []);
    setProjetos(projetosData || []);
    setColaboradores(colabData || []);
    setTiposAcao(configData?.valor || ['Superendividamento','RMC','RCC','Revisão de Contrato','DBA','Gestão de Passivo','Parcelamento Dívida do Agro','GPX','Outros']);
    setCarregando(false);

    // Deep-link vindo do Painel de Prazos (/casos?abrir=<id>) — abre o caso direto
    const abrirId = searchParams.get('abrir');
    if (abrirId) {
      const alvo = (casosData || []).find(c => c.id === abrirId);
      if (alvo) abrirEditarCasoView(alvo);
    }
  }

  function abrirEditarCasoView(caso) {
    setCasoAberto(caso);
    setAbaPainel('andamento');
  }

  const casosFiltrados = casos.filter(c => {
    const projOk = !filtroProjeto || c.projeto_id === filtroProjeto;
    const tipoOk = !filtroTipoAcao || (c.tipos_acao||[]).includes(filtroTipoAcao);
    return projOk && tipoOk;
  });

  function nomeProjeto(id) { return projetos.find(p=>p.id===id)?.nome || '—'; }
  function corProjeto(id)  { return projetos.find(p=>p.id===id)?.cor || '#9B7FD4'; }

  function abrirNovoCaso() {
    setCasoEditando(null); setForm(CASO_VAZIO); setErroSalvar(''); setModalCaso(true);
  }
  function abrirEditarCaso(caso) {
    setCasoEditando(caso);
    setForm({ ...CASO_VAZIO, ...caso, tipos_acao: caso.tipos_acao||[], etapas_extra: caso.etapas_extra||[], notas: caso.notas||[] });
    setErroSalvar(''); setModalCaso(true);
  }

  async function salvarCaso() {
    if (!form.nome_cliente?.trim()) { setErroSalvar('Nome do cliente é obrigatório.'); return; }
    setSalvando(true); setErroSalvar('');
    const pendente = !form.projeto_id || !form.nome_cliente || (form.tipos_acao||[]).length === 0;
    // Payload com apenas os campos que existem na tabela casos
    const payload = {
      projeto_id:               form.projeto_id || null,
      nome_cliente:             form.nome_cliente,
      tipos_acao:               form.tipos_acao || [],
      link_drive:               form.link_drive || null,
      resumo:                   form.resumo || null,
      responsavel:              form.responsavel || null,
      dt_contrato_assinado:     form.dt_contrato_assinado || null,
      dt_pagamento_realizado:   form.dt_pagamento_realizado || null,
      dt_procuracao_hipossuficiencia: form.dt_procuracao_hipossuficiencia || null,
      dt_documentos_entregues:  form.dt_documentos_entregues || null,
      dt_notificacao_enviada:   form.dt_notificacao_enviada || null,
      dt_acao_protocolada:      form.dt_acao_protocolada || null,
      dt_impugnacao:            form.dt_impugnacao || null,
      dt_sentenca:              form.dt_sentenca || null,
      etapas_extra:             form.etapas_extra || [],
      notas:                    form.notas || [],
      parceria:                 form.parceria || false,
      parceiro_nome:            form.parceiro_nome || null,
      honorarios_exito:         form.honorarios_exito || null,
      honorarios_sucumbencia:   form.honorarios_sucumbencia || null,
      status:                   form.status || 'Contrato Assinado',
      pendente_preenchimento:   pendente,
    };
    if (casoEditando) {
      const { data, error } = await supabase.from('casos').update(payload).eq('id', casoEditando.id).select().single();
      if (error) { setErroSalvar(`Erro ao salvar: ${error.message}`); setSalvando(false); return; }
      if (data) { setCasos(casos.map(c => c.id===data.id?data:c)); if (casoAberto?.id===data.id) setCasoAberto(data); }
    } else {
      const { data, error } = await supabase.from('casos').insert([payload]).select().single();
      if (error) { setErroSalvar(`Erro ao salvar: ${error.message}`); setSalvando(false); return; }
      if (data) setCasos([data, ...casos]);
    }
    setSalvando(false); setModalCaso(false); setCasoEditando(null);
  }

  function toggleTipoAcao(tipo) {
    const atual = form.tipos_acao || [];
    setForm({ ...form, tipos_acao: atual.includes(tipo) ? atual.filter(t=>t!==tipo) : [...atual, tipo] });
  }

  async function moverCaso(caso, novoStatus, e) {
    if (e) e.stopPropagation();
    const { data, error } = await supabase.from('casos').update({ status: novoStatus }).eq('id', caso.id).select().single();
    if (!error && data) {
      setCasos(casos.map(c => c.id===data.id?data:c));
      if (casoAberto?.id===data.id) setCasoAberto(data);
    }
  }

  async function marcarEtapa(caso, campo, dataStr) {
    const payload = { [campo]: dataStr || null };
    const { data, error } = await supabase.from('casos').update(payload).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
  }

  async function adicionarNota(caso) {
    if (!novaNota.trim()) return;
    const notas = [...(caso.notas||[]), { texto: novaNota.trim(), data_hora: agora() }];
    const { data, error } = await supabase.from('casos').update({ notas }).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
    setNovaNota('');
  }

  async function adicionarEtapaExtra(caso) {
    if (!novaEtapaExtra.trim()) return;
    const etapas_extra = [...(caso.etapas_extra||[]), { label: novaEtapaExtra.trim(), data: null, visivel_cliente: novaEtapaVisivelCliente }];
    const { data, error } = await supabase.from('casos').update({ etapas_extra }).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
    setNovaEtapaExtra(''); setNovaEtapaVisivelCliente(true);
  }

  async function toggleEtapaExtra(caso, idx) {
    const etapas_extra = (caso.etapas_extra||[]).map((e,i) => i===idx ? { ...e, data: e.data ? null : hoje() } : e);
    const { data, error } = await supabase.from('casos').update({ etapas_extra }).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
  }

  async function toggleVisibilidadeEtapaExtra(caso, idx) {
    const etapas_extra = (caso.etapas_extra||[]).map((e,i) => i===idx ? { ...e, visivel_cliente: e.visivel_cliente === false } : e);
    const { data, error } = await supabase.from('casos').update({ etapas_extra }).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
  }

  async function toggleVisibilidadeEtapaFixa(caso, campo) {
    const atual = new Set(caso.etapas_ocultas_cliente || []);
    if (atual.has(campo)) atual.delete(campo); else atual.add(campo);
    const { data, error } = await supabase.from('casos').update({ etapas_ocultas_cliente: [...atual] }).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
  }

  async function salvarCampo(caso, campo, valor) {
    const v = valor === '' ? null : isNaN(Number(valor)) ? valor : Number(valor);
    const { data, error } = await supabase.from('casos').update({ [campo]: v }).eq('id', caso.id).select().single();
    if (!error && data) { setCasos(casos.map(c => c.id===data.id?data:c)); setCasoAberto(data); }
  }

  async function excluirCaso(caso) {
    await supabase.from('cobrancas_casos').delete().eq('caso_id', caso.id);
    const { error } = await supabase.from('casos').delete().eq('id', caso.id);
    if (!error) {
      setCasos(casos.filter(c => c.id !== caso.id));
      setCobrancas(cobrancas.filter(c => c.caso_id !== caso.id));
      setCasoAberto(null);
      setConfirmDeleteCaso(null);
    }
  }

  const FORMAS_PAG = [
    { val:'pix',           label:'PIX',                  icone:'⚡', parcelas: false },
    { val:'transferencia', label:'Transferência',         icone:'🏦', parcelas: false },
    { val:'boleto',        label:'Boleto à vista',        icone:'📄', parcelas: false },
    { val:'boleto_parc',   label:'Boleto parcelado',      icone:'📋', parcelas: true  },
    { val:'cartao',        label:'Cartão de crédito',     icone:'💳', parcelas: true  },
    { val:'cheque',        label:'Cheque',                icone:'✏️', parcelas: false },
  ];

  function gerarDatasCobranca() {
    const { valor_total, forma_pagamento, n_parcelas, data_primeira } = formCobranca;
    if (!valor_total || !data_primeira) return [];
    const forma = FORMAS_PAG.find(f => f.val === forma_pagamento);
    const n = forma?.parcelas ? Math.max(1, Number(n_parcelas)||1) : 1;
    const total = Number(valor_total);
    const valorParcela = total / n;
    const datas = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(data_primeira + 'T12:00:00');
      d.setMonth(d.getMonth() + i);
      datas.push({ data: d.toISOString().split('T')[0], valor: valorParcela, parcela: i+1, total: n });
    }
    return datas;
  }

  function abrirModalCobranca(caso, e) {
    if (e) e.stopPropagation();
    const total = (Number(caso.honorarios_exito)||0) + (Number(caso.honorarios_sucumbencia)||0);
    setFormCobranca({ valor_total: total || '', forma_pagamento:'pix', n_parcelas:1, data_primeira: hoje(), pct_nosso:30 });
    setCasoAberto(caso); setModalCobranca(true);
  }

  async function salvarCobranca() {
    const datas = gerarDatasCobranca();
    if (!casoAberto || datas.length === 0) return;
    const forma = FORMAS_PAG.find(f => f.val === formCobranca.forma_pagamento);
    const n = datas.length;
    const insertsData = datas.map(d => ({
      caso_id:      casoAberto.id,
      projeto_id:   casoAberto.projeto_id,
      descricao:    n > 1
        ? `${casoAberto.nome_cliente} — ${forma?.label} ${d.parcela}/${n}`
        : `${casoAberto.nome_cliente} — ${forma?.label}`,
      data_prevista: d.data,
      valor_total:   d.valor,
      pct_nosso:     Number(formCobranca.pct_nosso) || 30,
    }));
    const { data, error } = await supabase.from('cobrancas_casos').insert(insertsData).select();
    if (error) {
      alert('Erro ao salvar cobrança (cobrancas_casos): ' + error.message + '\n\nRode no Supabase SQL Editor:\nALTER TABLE cobrancas_casos DISABLE ROW LEVEL SECURITY;');
      return;
    }
    if (!data || data.length === 0) {
      alert('Cobrança não foi salva (RLS bloqueou silenciosamente).\n\nRode no Supabase SQL Editor:\nALTER TABLE cobrancas_casos DISABLE ROW LEVEL SECURITY;\nALTER TABLE financeiro DISABLE ROW LEVEL SECURITY;');
      return;
    }

    setCobrancas(prev => [...prev, ...data]);
    await supabase.from('casos').update({ cobranca_gerada: true }).eq('id', casoAberto.id);

    // vai para o calendário no mês da primeira cobrança
    const primeiraData = new Date(datas[0].data + 'T12:00:00');
    setMesCalendario({ ano: primeiraData.getFullYear(), mes: primeiraData.getMonth() });
    setAba('cobrancas');
    setModalCobranca(false);
  }

  async function marcarCobrancaRecebida(cob) {
    const { data, error } = await supabase.from('cobrancas_casos').update({ status:'Recebido', data_recebimento: hoje() }).eq('id', cob.id).select().single();
    if (!error && data) setCobrancas(cobrancas.map(c => c.id===data.id?data:c));
  }

  async function lancarNoFinanceiro(cob) {
    const valorNosso = cob.valor_total * (cob.pct_nosso/100);
    const { error } = await supabase.from('financeiro').insert([{
      tipo: 'entrada', descricao: cob.descricao, valor: valorNosso,
      data_lancamento: cob.data_prevista, status: cob.status==='Recebido'?'Recebido':'Pendente',
      projeto: nomeProjeto(cob.projeto_id),
    }]);
    if (!error) {
      const { data } = await supabase.from('cobrancas_casos').update({ lancado_financeiro:true }).eq('id', cob.id).select().single();
      if (data) setCobrancas(cobrancas.map(c => c.id===data.id?data:c));
    }
  }

  async function excluirCobranca(cob) {
    if (!window.confirm(`Excluir a cobrança "${cob.descricao}" (${fmtMoeda(cob.valor_total)})?\n\nEssa ação não pode ser desfeita.`)) return;
    const { error } = await supabase.from('cobrancas_casos').delete().eq('id', cob.id);
    if (!error) setCobrancas(prev => prev.filter(c => c.id !== cob.id));
  }

  async function excluirCobrancaInteira(casoId) {
    const doCaso = cobrancas.filter(c => c.caso_id === casoId);
    if (doCaso.length === 0) return;
    const total = doCaso.reduce((s,c) => s + (Number(c.valor_total)||0), 0);
    if (!window.confirm(`Excluir TODAS as ${doCaso.length} parcela(s) dessa cobrança (total ${fmtMoeda(total)})?\n\nEssa ação não pode ser desfeita.`)) return;
    const { error } = await supabase.from('cobrancas_casos').delete().eq('caso_id', casoId);
    if (!error) setCobrancas(prev => prev.filter(c => c.caso_id !== casoId));
  }

  function iniciarEdicaoCobranca(cob) {
    setEditandoCobrancaId(cob.id);
    setEditCobranca({ descricao: cob.descricao || '', valor_total: cob.valor_total || '', data_prevista: cob.data_prevista || '' });
  }

  async function salvarEdicaoCobranca(cobId) {
    const { data, error } = await supabase.from('cobrancas_casos')
      .update({
        descricao:     editCobranca.descricao,
        valor_total:   Number(editCobranca.valor_total) || 0,
        data_prevista: editCobranca.data_prevista,
      })
      .eq('id', cobId).select().single();
    if (!error && data) {
      setCobrancas(prev => prev.map(c => c.id === data.id ? data : c));
      setEditandoCobrancaId(null);
    }
  }

  const cobrancasFiltradas = cobrancas.filter(c => !filtroProjeto || c.projeto_id===filtroProjeto);

  const { ano, mes } = mesCalendario;
  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDia   = new Date(ano, mes+1, 0);
  const diasNoMes   = ultimoDia.getDate();
  const offsetSemana = primeiroDia.getDay();
  const celulas = [...Array(offsetSemana).fill(null), ...Array.from({length:diasNoMes}, (_,i)=>i+1)];
  const nomesMes = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  function cobrancasDoDia(dia) {
    if (!dia) return [];
    const dataStr = `${ano}-${String(mes+1).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    return cobrancasFiltradas.filter(c => c.data_prevista === dataStr);
  }

  if (carregando) return <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>Carregando casos...</div>;

  const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif', boxSizing:'border-box' };

  return (
    <div>
      <div className="section-header"><h2 className="section-title">Casos</h2><div className="section-line" /></div>

      <div style={{ display:'flex', gap:16, marginBottom:18, alignItems:'center', flexWrap:'wrap' }}>
        <div className="tabs-wrap">
          <button onClick={() => setAba('casos')}     className={`tab-btn${aba==='casos'?     ' active':''}`}>Casos</button>
          <button onClick={() => setAba('cobrancas')} className={`tab-btn${aba==='cobrancas'?' active':''}`}>Calendário de Cobranças</button>
        </div>
        <div style={{ flex:1 }} />
        {aba==='casos' && (
          <button onClick={abrirNovoCaso}
            style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 22px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>
            + Novo Caso
          </button>
        )}
      </div>

      <div className="filter-bar">
        <select value={filtroProjeto} onChange={e => setFiltroProjeto(e.target.value)} className={`chip-select${filtroProjeto?' active':''}`}>
          <option value=''>Projeto</option>
          {projetos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
        {aba==='casos' && (
          <select value={filtroTipoAcao} onChange={e => setFiltroTipoAcao(e.target.value)} className={`chip-select${filtroTipoAcao?' active':''}`}>
            <option value=''>Tipo de ação</option>
            {tiposAcaoDisponiveis.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {(filtroProjeto||filtroTipoAcao) && (
          <button onClick={() => { setFiltroProjeto(''); setFiltroTipoAcao(''); }}
            style={{ padding:'7px 12px', border:'none', borderRadius:10, background:'#F3EBFF', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
            ✕ Limpar
          </button>
        )}
        <span style={{ marginLeft:'auto', fontSize:12, color:'#7B5EA7', fontWeight:600 }}>{casosFiltrados.length} caso(s)</span>
      </div>

      {/* ── Kanban de casos ── */}
      {aba === 'casos' && (
        <div style={{ display:'grid', gridTemplateColumns:`repeat(${STATUS_COLUNAS.length},1fr)`, gap:12, overflowX:'auto' }}>
          {STATUS_COLUNAS.map((coluna, idxCol) => {
            const itens = casosFiltrados.filter(c => statusDoCaso(c) === coluna);
            const cc = COR_COLUNA[coluna];
            const anterior = STATUS_COLUNAS[idxCol - 1];
            const proximo  = STATUS_COLUNAS[idxCol + 1];
            return (
              <div key={coluna} style={{ background:'rgba(243,235,255,0.35)', borderRadius:14, padding:12, minHeight:220, border:'1.5px solid rgba(107,33,168,0.07)', minWidth:220 }}>
                {/* cabeçalho da coluna */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <div style={{ width:9, height:9, borderRadius:'50%', background:cc.cor }} />
                    <span style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', textTransform:'uppercase', letterSpacing:'0.05em', lineHeight:1.2 }}>{coluna}</span>
                  </div>
                  <span style={{ background:cc.grad, color:'white', borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{itens.length}</span>
                </div>

                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {itens.map(caso => {
                    const concluidas = ETAPAS_CHECKLIST.filter(e => caso[e.campo]).length;
                    const honTotal   = (Number(caso.honorarios_exito)||0) + (Number(caso.honorarios_sucumbencia)||0);
                    const cobsCaso   = cobrancas.filter(c => c.caso_id === caso.id);
                    const nCobs      = cobsCaso.length;
                    const cobsAtrasadas = cobsCaso.filter(c => c.status !== 'Recebido' && diasAtraso(c.data_prevista) > 0);
                    const maxAtraso  = cobsAtrasadas.reduce((max, c) => Math.max(max, diasAtraso(c.data_prevista)), 0);
                    const temAtraso  = cobsAtrasadas.length > 0;
                    return (
                      <div key={caso.id} className="card" style={{ padding:12, borderLeft:`3px solid ${temAtraso ? '#dc2626' : caso.pendente_preenchimento ? '#E05454' : cc.cor}`, background: temAtraso ? '#fff8f8' : 'white' }}>

                        {/* conteúdo clicável */}
                        <div onClick={() => { setCasoAberto(caso); setAbaPainel('andamento'); }} style={{ cursor:'pointer', marginBottom:8 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:3 }}>
                            <div style={{ fontWeight:700, fontSize:12, color:'#1E0A3C', flex:1, lineHeight:1.3 }}>{caso.nome_cliente}</div>
                            {caso.pendente_preenchimento && <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:8, fontWeight:700, padding:'1px 5px', borderRadius:8, flexShrink:0, marginLeft:4 }}>!</span>}
                          </div>
                          <div style={{ fontSize:10, color:'#7B5EA7', marginBottom:5 }}>{nomeProjeto(caso.projeto_id)}</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:3, marginBottom:6 }}>
                            {(caso.tipos_acao||[]).slice(0,1).map(t => <span key={t} style={{ background:'#F3EBFF', color:'#7B2FBE', fontSize:8, fontWeight:600, padding:'2px 6px', borderRadius:8 }}>{t}</span>)}
                            {(caso.tipos_acao||[]).length>1 && <span style={{ fontSize:8, color:'#9ca3af' }}>+{(caso.tipos_acao||[]).length-1}</span>}
                          </div>
                          {caso.parceria && <div style={{ fontSize:9, color:'#C9A84C', marginBottom:4, fontWeight:600 }}>🤝 {caso.parceiro_nome||'Parceiro'}</div>}
                          {honTotal > 0 && <div style={{ fontSize:10, fontWeight:700, color:'#b45309', marginBottom:4 }}>💰 {fmtMoeda(honTotal)}</div>}
                          {temAtraso && <div style={{ background:'#fee2e2', color:'#dc2626', fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:8, marginBottom:5, display:'inline-block' }}>⚠ Cobrança em atraso há {maxAtraso} dia{maxAtraso>1?'s':''}</div>}
                          {nCobs > 0 && <div style={{ fontSize:9, color:'#059669', fontWeight:600, marginBottom:4 }}>📋 {nCobs} cobrança(s)</div>}
                          <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:5 }}>
                            <span style={{ fontSize:8, fontWeight:700, padding:'2px 6px', borderRadius:8, background: caso.dt_pagamento_realizado ? '#dcfce7' : '#fef3c7', color: caso.dt_pagamento_realizado ? '#16a34a' : '#92400e' }}>
                              {caso.dt_pagamento_realizado ? '✓ Pago' : '⏳ Pagamento'}
                            </span>
                            <span style={{ fontSize:8, fontWeight:700, padding:'2px 6px', borderRadius:8, background: caso.dt_procuracao_hipossuficiencia ? '#dcfce7' : '#fef3c7', color: caso.dt_procuracao_hipossuficiencia ? '#16a34a' : '#92400e' }}>
                              {caso.dt_procuracao_hipossuficiencia ? '✓ Procuração' : '⏳ Procuração'}
                            </span>
                          </div>
                          <div style={{ height:3, background:'rgba(107,33,168,0.1)', borderRadius:4, marginBottom:3 }}>
                            <div style={{ width:`${(concluidas/ETAPAS_CHECKLIST.length)*100}%`, height:'100%', background:'linear-gradient(90deg,#7B2FBE,#a855f7)', borderRadius:4 }} />
                          </div>
                          <div style={{ fontSize:9, color:'#9ca3af' }}>{concluidas}/{ETAPAS_CHECKLIST.length} etapas</div>
                        </div>

                        {/* botões de ação */}
                        <div style={{ display:'flex', gap:5 }}>
                          {/* avançar etapa */}
                          {proximo && (
                            <button onClick={e => moverCaso(caso, proximo, e)}
                              style={{ flex:1, background:cc.grad, color:'white', border:'none', borderRadius:7, padding:'5px 0', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'Poppins,sans-serif' }}
                              title={`Mover para: ${proximo}`}>
                              → Avançar
                            </button>
                          )}
                          {/* voltar etapa */}
                          {anterior && (
                            <button onClick={e => moverCaso(caso, anterior, e)}
                              style={{ background:'transparent', color:'#9ca3af', border:'1px solid #e5e7eb', borderRadius:7, padding:'5px 8px', fontSize:10, cursor:'pointer' }}
                              title={`Voltar para: ${anterior}`}>
                              ←
                            </button>
                          )}
                          {/* cobrança */}
                          <button onClick={e => abrirModalCobranca(caso, e)}
                            style={{ background:'#fef3c7', color:'#92400e', border:'1px solid #fde68a', borderRadius:7, padding:'5px 8px', fontSize:11, cursor:'pointer' }}
                            title="Gerar cobrança">
                            💰
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {itens.length===0 && <div style={{ textAlign:'center', padding:'24px 0', fontSize:11, color:'rgba(107,33,168,0.35)' }}>Nenhum caso</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Calendário de cobranças ── */}
      {aba === 'cobrancas' && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
            <button onClick={() => setMesCalendario(m => m.mes===0?{ano:m.ano-1,mes:11}:{...m,mes:m.mes-1})}
              style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:14, color:'#7B5EA7' }}>←</button>
            <span style={{ fontWeight:700, fontSize:15, color:'#1E0A3C', fontFamily:'Playfair Display,serif' }}>{nomesMes[mes]} {ano}</span>
            <button onClick={() => setMesCalendario(m => m.mes===11?{ano:m.ano+1,mes:0}:{...m,mes:m.mes+1})}
              style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:14, color:'#7B5EA7' }}>→</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginBottom:6 }}>
            {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'#7B5EA7' }}>{d}</div>)}
          </div>
          {/* KPIs do mês */}
          {(() => {
            const cobMes = cobrancasFiltradas.filter(c => {
              const d = new Date(c.data_prevista + 'T00:00');
              return d.getFullYear() === ano && d.getMonth() === mes;
            });
            const totalMes   = cobMes.reduce((s,c) => s + (c.valor_total||0), 0);
            const recebido   = cobMes.filter(c => c.status==='Recebido').reduce((s,c) => s + (c.valor_total||0), 0);
            const pendente   = totalMes - recebido;
            const nossoMes   = cobMes.reduce((s,c) => s + (c.valor_total||0) * ((c.pct_nosso||30)/100), 0);
            if (cobMes.length === 0) return null;
            return (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
                {[
                  { label:'Total do mês', val: fmtMoeda(totalMes), cor:'#7B2FBE' },
                  { label:'Recebido',     val: fmtMoeda(recebido),  cor:'#16a34a' },
                  { label:'Pendente',     val: fmtMoeda(pendente),  cor:'#b45309' },
                  { label:'Nosso (30%)',  val: fmtMoeda(nossoMes),  cor:'#3F0051' },
                ].map(k => (
                  <div key={k.label} style={{ background:'white', borderRadius:12, padding:'12px 14px', border:'1px solid rgba(107,33,168,0.08)', textAlign:'center' }}>
                    <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:600, marginBottom:4 }}>{k.label}</div>
                    <div style={{ fontSize:16, fontWeight:800, color:k.cor, fontFamily:'Playfair Display,serif' }}>{k.val}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginBottom:24 }}>
            {celulas.map((dia, i) => {
              const cobs = cobrancasDoDia(dia);
              const temPendente = cobs.some(c => c.status !== 'Recebido');
              const todosRecebidos = cobs.length > 0 && !temPendente;
              const hojeStr = hoje();
              const diaStr = dia ? `${ano}-${String(mes+1).padStart(2,'0')}-${String(dia).padStart(2,'0')}` : null;
              const ePassado = diaStr && diaStr < hojeStr;
              const temAtrasado = cobs.some(c => c.status !== 'Recebido' && ePassado);
              return (
                <div key={i} style={{ minHeight:90, background: dia?(temAtrasado?'#fff1f2':todosRecebidos?'#f0fdf4':temPendente?'#fffbeb':'white'):'transparent', borderRadius:10, padding:8, border: dia?`1px solid ${temAtrasado?'#fca5a5':todosRecebidos?'#bbf7d0':temPendente?'#fde68a':'rgba(107,33,168,0.08)'}`:'none' }}>
                  {dia && <>
                    <div style={{ fontSize:11, fontWeight:700, color: temAtrasado?'#dc2626':temPendente?'#92400e':todosRecebidos?'#16a34a':'#7B5EA7', marginBottom:4 }}>{dia}</div>
                    {cobs.map(c => {
                      const dias = c.status !== 'Recebido' ? diasAtraso(c.data_prevista) : 0;
                      const atrasado = dias > 0;
                      return (
                        <div key={c.id} onClick={() => { setCasoAberto(casos.find(x=>x.id===c.caso_id)); setAbaPainel('valores'); }}
                          style={{ background: c.status==='Recebido'?'rgba(22,163,74,0.12)':atrasado?'rgba(220,38,38,0.08)':'rgba(180,83,9,0.1)', borderRadius:6, padding:'5px 7px', marginBottom:4, cursor:'pointer', borderLeft:`2px solid ${c.status==='Recebido'?'#16a34a':atrasado?'#dc2626':'#b45309'}` }}>
                          <div style={{ fontSize:10, fontWeight:800, color:c.status==='Recebido'?'#16a34a':atrasado?'#dc2626':'#92400e' }}>{fmtMoeda(c.valor_total)}</div>
                          <div style={{ fontSize:9, color:'#6B7280', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.descricao.split('—')[0].trim()}</div>
                          {c.status==='Recebido' && <div style={{ fontSize:8, color:'#16a34a', fontWeight:700 }}>✓ recebido</div>}
                          {atrasado && <div style={{ fontSize:8, color:'#dc2626', fontWeight:700 }}>⚠ {dias} dia{dias>1?'s':''} em atraso</div>}
                        </div>
                      );
                    })}
                  </>}
                </div>
              );
            })}
          </div>

          {/* Lista de cobranças do mês */}
          <div style={{ background:'white', borderRadius:16, overflow:'hidden', border:'1px solid rgba(107,33,168,0.08)' }}>
            <div style={{ padding:'14px 18px', background:'linear-gradient(135deg,#FAFAFF,#F5EEFF)', borderBottom:'1px solid rgba(107,33,168,0.08)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#3F0051' }}>Todas as cobranças</span>
              <span style={{ fontSize:11, color:'#7B5EA7' }}>{cobrancasFiltradas.length} registro(s)</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
              {cobrancasFiltradas.length === 0 && (
                <div style={{ textAlign:'center', padding:40, color:'#9ca3af', fontSize:13 }}>
                  Nenhuma cobrança ainda. Clique em "💰 Gerar Cobrança" em um caso para começar.
                </div>
              )}
              {cobrancasFiltradas.map((c, i) => {
                const valorNosso = c.valor_total * ((c.pct_nosso||30)/100);
                const dias = c.status !== 'Recebido' ? diasAtraso(c.data_prevista) : 0;
                const atrasado = dias > 0;
                if (editandoCobrancaId === c.id) {
                  return (
                    <div key={c.id} style={{ display:'flex', flexDirection:'column', gap:8, padding:'14px 18px', borderBottom:'1px solid rgba(107,33,168,0.05)', background:'#F5EEFF' }}>
                      <input value={editCobranca.descricao} onChange={e => setEditCobranca({...editCobranca, descricao:e.target.value})}
                        placeholder="Descrição" style={{ padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, fontSize:12, fontFamily:'Poppins,sans-serif' }} />
                      <div style={{ display:'flex', gap:8 }}>
                        <input type="number" step="0.01" value={editCobranca.valor_total} onChange={e => setEditCobranca({...editCobranca, valor_total:e.target.value})}
                          placeholder="Valor" style={{ flex:1, padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, fontSize:12, fontFamily:'Poppins,sans-serif' }} />
                        <input type="date" value={editCobranca.data_prevista} onChange={e => setEditCobranca({...editCobranca, data_prevista:e.target.value})}
                          style={{ flex:1, padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, fontSize:12, fontFamily:'Poppins,sans-serif' }} />
                      </div>
                      <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                        <button onClick={() => setEditandoCobrancaId(null)}
                          style={{ background:'white', color:'#7B5EA7', border:'1px solid rgba(107,33,168,0.2)', borderRadius:6, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700 }}>Cancelar</button>
                        <button onClick={() => salvarEdicaoCobranca(c.id)}
                          style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', borderRadius:6, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700 }}>Salvar</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={c.id} style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:12, padding:'14px 18px', borderBottom:'1px solid rgba(107,33,168,0.05)', background: atrasado?'#fff8f8':i%2===0?'white':'#FAFAFF', alignItems:'center', borderLeft: atrasado?'4px solid #dc2626':'none' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3, flexWrap:'wrap' }}>
                        <span style={{ fontSize:13, fontWeight:600, color:'#1E0A3C' }}>{c.descricao}</span>
                        {atrasado && <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:10 }}>⚠ {dias} dia{dias>1?'s':''} em atraso</span>}
                      </div>
                      <div style={{ display:'flex', gap:10, fontSize:11, color:'#7B5EA7', flexWrap:'wrap' }}>
                        <span style={{ color: atrasado?'#dc2626':'#7B5EA7', fontWeight: atrasado?700:400 }}>📅 {atrasado?'Venceu':'Vence'} {new Date(c.data_prevista+'T00:00').toLocaleDateString('pt-BR')}</span>
                        <span>🏢 {nomeProjeto(c.projeto_id)}</span>
                        <span style={{ color:'#16a34a', fontWeight:700 }}>Nosso: {fmtMoeda(valorNosso)}</span>
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:16, fontWeight:800, color:'#3F0051', fontFamily:'Playfair Display,serif' }}>{fmtMoeda(c.valor_total)}</div>
                        <span style={{ background:c.status==='Recebido'?'#dcfce7':'#fef3c7', color:c.status==='Recebido'?'#16a34a':'#92400e', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700 }}>{c.status || 'Pendente'}</span>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                        {c.status!=='Recebido' && (
                          <button onClick={() => marcarCobrancaRecebida(c)}
                            style={{ background:'#16a34a', color:'white', border:'none', borderRadius:7, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700, whiteSpace:'nowrap' }}>✓ Recebido</button>
                        )}
                        {!c.lancado_financeiro ? (
                          <button onClick={() => lancarNoFinanceiro(c)}
                            style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', borderRadius:7, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700, whiteSpace:'nowrap' }}>→ Financeiro</button>
                        ) : (
                          <span style={{ fontSize:10, color:'#059669', fontWeight:700, textAlign:'center' }}>✓ lançado</span>
                        )}
                        <div style={{ display:'flex', gap:5 }}>
                          <button onClick={() => iniciarEdicaoCobranca(c)} title="Editar"
                            style={{ flex:1, background:'white', color:'#7B5EA7', border:'1px solid rgba(107,33,168,0.2)', borderRadius:6, padding:'5px 0', fontSize:11, cursor:'pointer', fontWeight:700 }}>✏️</button>
                          <button onClick={() => excluirCobranca(c)} title="Excluir"
                            style={{ flex:1, background:'white', color:'#dc2626', border:'1px solid #fecaca', borderRadius:6, padding:'5px 0', fontSize:11, cursor:'pointer', fontWeight:700 }}>🗑</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Painel lateral do caso ── */}
      {casoAberto && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex' }}>
          <div style={{ flex:1, background:'rgba(0,0,0,0.4)' }} onClick={() => setCasoAberto(null)} />
          <div style={{ width:580, background:'white', borderLeft:'1px solid rgba(107,33,168,0.1)', overflowY:'auto', padding:28, boxShadow:'-4px 0 32px rgba(0,0,0,0.18)' }}>

            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <h3 style={{ margin:0, color:'#1E0A3C', fontSize:18, fontFamily:'Playfair Display,serif' }}>{casoAberto.nome_cliente}</h3>
                <div style={{ fontSize:12, color:'#7B5EA7', marginTop:4 }}>
                  {nomeProjeto(casoAberto.projeto_id)}
                  {casoAberto.responsavel && <> · {casoAberto.responsavel}</>}
                  {casoAberto.link_drive && <> · <a href={casoAberto.link_drive} target="_blank" rel="noreferrer" style={{ color:'#4A90D9' }}>📁 Drive</a></>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                <a href={`https://revisao-contratual-bancaria-production.up.railway.app/pecas?casoId=${casoAberto.id}`} target="_blank" rel="noreferrer"
                  title="Gerar notificação/petição/réplica pra este caso"
                  style={{ padding:'7px 14px', borderRadius:9, border:'1.5px solid rgba(107,33,168,0.2)', background:'white', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif', textDecoration:'none', display:'inline-flex', alignItems:'center' }}>
                  ⚖ Gerar peça
                </a>
                <button onClick={() => copiarLinkCliente(casoAberto)} disabled={!casoAberto.cliente_token}
                  title="Link pro cliente acompanhar o próprio caso"
                  style={{ padding:'7px 14px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
                  {linkCopiado ? '✓ copiado' : '🔗 Link do cliente'}
                </button>
                <button onClick={() => abrirEditarCaso(casoAberto)}
                  style={{ padding:'7px 14px', borderRadius:9, border:'1.5px solid rgba(107,33,168,0.2)', background:'white', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
                  ✏ Editar
                </button>
                <button onClick={() => setConfirmDeleteCaso(casoAberto)}
                  style={{ padding:'7px 14px', borderRadius:9, border:'1.5px solid rgba(220,38,38,0.3)', background:'white', color:'#dc2626', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
                  🗑 Excluir
                </button>
                <button onClick={() => setCasoAberto(null)} style={{ background:'transparent', border:'none', fontSize:22, cursor:'pointer', color:'#9ca3af', lineHeight:1 }}>×</button>
              </div>
            </div>

            {casoAberto.pendente_preenchimento && (
              <div style={{ background:'#fee2e2', border:'1px solid #dc2626', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#dc2626', fontWeight:600 }}>
                ⚠️ Dados incompletos — preencha projeto e tipo de ação clicando em Editar.
              </div>
            )}

            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
              {(casoAberto.tipos_acao||[]).map(t => <span key={t} style={{ background:'#F3EBFF', color:'#7B2FBE', fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12 }}>{t}</span>)}
              {casoAberto.parceria && <span style={{ background:'#FFFBEB', color:'#C9A84C', fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12 }}>🤝 {casoAberto.parceiro_nome || 'Parceiro'}</span>}
            </div>

            {/* Tabs do painel */}
            <div className="tabs-wrap" style={{ marginBottom:20 }}>
              <button onClick={() => setAbaPainel('andamento')} className={`tab-btn${abaPainel==='andamento'?' active':''}`}>Andamento</button>
              <button onClick={() => setAbaPainel('valores')}   className={`tab-btn${abaPainel==='valores'?  ' active':''}`}>Valores & Parcelas</button>
              <button onClick={() => setAbaPainel('notas')}     className={`tab-btn${abaPainel==='notas'?    ' active':''}`}>Notas {(casoAberto.notas||[]).length > 0 ? `(${casoAberto.notas.length})` : ''}</button>
              <button onClick={() => setAbaPainel('documentos')} className={`tab-btn${abaPainel==='documentos'?' active':''}`}>Documentos {documentos.length > 0 ? `(${documentos.length})` : ''}</button>
              <button onClick={() => setAbaPainel('atualizacoes')} className={`tab-btn${abaPainel==='atualizacoes'?' active':''}`}>Atualizações {atualizacoes.length > 0 ? `(${atualizacoes.length})` : ''}</button>
              <button onClick={() => setAbaPainel('pecas')} className={`tab-btn${abaPainel==='pecas'?' active':''}`}>⚖ Peças {pecas.length > 0 ? `(${pecas.length})` : ''}</button>
            </div>

            {/* ── Aba Andamento ── */}
            {abaPainel === 'andamento' && (
              <>
                {casoAberto.resumo && (
                  <div style={{ background:'#FAF8FF', border:'1px solid rgba(107,33,168,0.1)', borderRadius:12, padding:14, marginBottom:14 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', marginBottom:6, textTransform:'uppercase' }}>Resumo</div>
                    <div style={{ fontSize:13, color:'#1E0A3C', whiteSpace:'pre-wrap', lineHeight:1.6 }}>{casoAberto.resumo}</div>
                  </div>
                )}
                <div className="card" style={{ padding:16, marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', textTransform:'uppercase' }}>Checklist de etapas</div>
                    <div style={{ fontSize:10.5, color:'#9ca3af' }}>👁 = o cliente vê essa etapa no link dele</div>
                  </div>
                  {ETAPAS_CHECKLIST.map(e => {
                    const oculta = (casoAberto.etapas_ocultas_cliente||[]).includes(e.campo);
                    return (
                      <div key={e.campo} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid rgba(107,33,168,0.05)' }}>
                        <span style={{ fontSize:16, color: casoAberto[e.campo] ? '#16a34a' : '#d1d5db', width:16, textAlign:'center' }}>{casoAberto[e.campo] ? '✓' : '○'}</span>
                        <span style={{ fontSize:13, color: casoAberto[e.campo] ? '#059669' : '#1E0A3C', flex:1, fontWeight: casoAberto[e.campo] ? 600 : 400 }}>{e.label}</span>
                        <button onClick={() => toggleVisibilidadeEtapaFixa(casoAberto, e.campo)}
                          title={oculta ? 'Só a equipe vê — clique pra mostrar ao cliente' : 'Cliente vê — clique pra ocultar'}
                          style={{ background: oculta ? '#f3f4f6' : '#F3EBFF', color: oculta ? '#9ca3af' : '#7B2FBE', border:'none', borderRadius:6, padding:'4px 9px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}>
                          {oculta ? '🙈 só interno' : '👁 cliente vê'}
                        </button>
                        <input type="date" defaultValue={casoAberto[e.campo] || ''}
                          onBlur={ev => marcarEtapa(casoAberto, e.campo, ev.target.value)}
                          style={{ padding:'5px 8px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:7, fontSize:11, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', width:145 }} />
                      </div>
                    );
                  })}
                  {(casoAberto.etapas_extra||[]).map((e, idx) => {
                    const visivel = e.visivel_cliente !== false;
                    return (
                      <div key={idx} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid rgba(107,33,168,0.05)' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', flex:1 }}>
                          <input type="checkbox" checked={!!e.data} onChange={() => toggleEtapaExtra(casoAberto, idx)} style={{ accentColor:'#7B2FBE', width:16, height:16 }} />
                          <span style={{ fontSize:13, color: e.data ? '#059669' : '#1E0A3C', flex:1 }}>{e.label}</span>
                          {e.data && <span style={{ fontSize:11, color:'#9ca3af' }}>{new Date(e.data+'T00:00').toLocaleDateString('pt-BR')}</span>}
                        </label>
                        <button onClick={() => toggleVisibilidadeEtapaExtra(casoAberto, idx)}
                          title={visivel ? 'Cliente vê — clique pra ocultar (prazo/decisão interna)' : 'Só a equipe vê — clique pra mostrar ao cliente'}
                          style={{ background: visivel ? '#F3EBFF' : '#f3f4f6', color: visivel ? '#7B2FBE' : '#9ca3af', border:'none', borderRadius:6, padding:'4px 9px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}>
                          {visivel ? '👁 cliente vê' : '🙈 só interno'}
                        </button>
                      </div>
                    );
                  })}
                  <div style={{ display:'flex', gap:8, marginTop:12, alignItems:'center', flexWrap:'wrap' }}>
                    <input placeholder="Adicionar etapa específica..." value={novaEtapaExtra} onChange={e => setNovaEtapaExtra(e.target.value)}
                      onKeyDown={e => e.key==='Enter' && adicionarEtapaExtra(casoAberto)}
                      style={{ flex:1, minWidth:160, padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#7B5EA7', cursor:'pointer', whiteSpace:'nowrap' }}>
                      <input type="checkbox" checked={novaEtapaVisivelCliente} onChange={e => setNovaEtapaVisivelCliente(e.target.checked)} style={{ accentColor:'#7B2FBE' }} />
                      cliente vê
                    </label>
                    <button onClick={() => adicionarEtapaExtra(casoAberto)}
                      style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700 }}>+</button>
                  </div>
                </div>
              </>
            )}

            {/* ── Aba Valores & Parcelas ── */}
            {abaPainel === 'valores' && (
              <>
                {/* Honorários */}
                <div className="card" style={{ padding:16, marginBottom:14 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', marginBottom:14, textTransform:'uppercase' }}>Honorários</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:'#7B5EA7', marginBottom:5, fontWeight:600 }}>Valor do contrato (R$)</label>
                      <input type="number" defaultValue={casoAberto.valor_contrato||''}
                        onBlur={e => salvarCampo(casoAberto, 'valor_contrato', e.target.value)}
                        style={{ ...inp }} placeholder="0,00" />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:'#7B5EA7', marginBottom:5, fontWeight:600 }}>Honorários de êxito (R$)</label>
                      <input type="number" defaultValue={casoAberto.honorarios_exito||''}
                        onBlur={e => salvarCampo(casoAberto, 'honorarios_exito', e.target.value)}
                        style={{ ...inp }} placeholder="0,00" />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:'#7B5EA7', marginBottom:5, fontWeight:600 }}>Honorários de êxito (%)</label>
                      <input type="number" step="0.01" defaultValue={casoAberto.honorarios_exito_pct||''}
                        onBlur={e => salvarCampo(casoAberto, 'honorarios_exito_pct', e.target.value)}
                        style={{ ...inp }} placeholder="Ex: 20" />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:'#7B5EA7', marginBottom:5, fontWeight:600 }}>Honorários de sucumbência (R$)</label>
                      <input type="number" defaultValue={casoAberto.honorarios_sucumbencia||''}
                        onBlur={e => salvarCampo(casoAberto, 'honorarios_sucumbencia', e.target.value)}
                        style={{ ...inp }} placeholder="0,00" />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:'#7B5EA7', marginBottom:5, fontWeight:600 }}>Honorários de sucumbência (%)</label>
                      <input type="number" step="0.01" defaultValue={casoAberto.honorarios_sucumbencia_pct||''}
                        onBlur={e => salvarCampo(casoAberto, 'honorarios_sucumbencia_pct', e.target.value)}
                        style={{ ...inp }} placeholder="Ex: 10" />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:'#7B5EA7', marginBottom:5, fontWeight:600 }}>Nº de parcelas do contrato</label>
                      <input type="number" defaultValue={casoAberto.n_parcelas||''}
                        onBlur={e => salvarCampo(casoAberto, 'n_parcelas', e.target.value)}
                        style={{ ...inp }} placeholder="Ex: 12" min="1" />
                    </div>
                  </div>

                  {/* Totalizador */}
                  {((Number(casoAberto.honorarios_exito)||0) + (Number(casoAberto.honorarios_sucumbencia)||0)) > 0 && (
                    <div style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', borderRadius:12, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)', fontWeight:600 }}>Total honorários (êxito + sucumbência)</span>
                      <span style={{ fontSize:18, fontWeight:800, color:'white', fontFamily:'Playfair Display,serif' }}>
                        {fmtMoeda((Number(casoAberto.honorarios_exito)||0) + (Number(casoAberto.honorarios_sucumbencia)||0))}
                      </span>
                    </div>
                  )}
                </div>

                {/* Cobranças deste caso */}
                <div className="card" style={{ padding:16, marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', textTransform:'uppercase' }}>Cobranças / Parcelas</div>
                    <button onClick={() => abrirModalCobranca(casoAberto)}
                      style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'6px 14px', borderRadius:8, cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:'Poppins,sans-serif' }}>
                      + Gerar cobrança
                    </button>
                  </div>
                  {cobrancas.filter(c => c.caso_id === casoAberto.id).length === 0 ? (
                    <div style={{ textAlign:'center', padding:'20px 0', color:'#9ca3af', fontSize:12 }}>Nenhuma cobrança gerada ainda.</div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {(() => {
                        const doCaso = cobrancas.filter(c => c.caso_id === casoAberto.id);
                        if (doCaso.length <= 1) return null;
                        const total = doCaso.reduce((s,c) => s + (Number(c.valor_total)||0), 0);
                        const recebido = doCaso.filter(c => c.status==='Recebido').reduce((s,c) => s + (Number(c.valor_total)||0), 0);
                        return (
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, padding:'10px 14px', background:'#FAF8FF', borderRadius:10, border:'1px solid rgba(107,33,168,0.1)', marginBottom:2 }}>
                            <div style={{ fontSize:11, color:'#3F0051' }}>
                              <b>{doCaso.length} parcelas</b> · Total: <b>{fmtMoeda(total)}</b> · Recebido: <span style={{ color:'#16a34a', fontWeight:700 }}>{fmtMoeda(recebido)}</span>
                            </div>
                            <button onClick={() => excluirCobrancaInteira(casoAberto.id)} title="Excluir todas as parcelas dessa cobrança"
                              style={{ background:'white', color:'#dc2626', border:'1px solid #fecaca', borderRadius:6, padding:'6px 10px', fontSize:10, cursor:'pointer', fontWeight:700, whiteSpace:'nowrap' }}>🗑 Excluir cobrança inteira</button>
                          </div>
                        );
                      })()}
                      {cobrancas.filter(c => c.caso_id === casoAberto.id).map((c, i) => {
                        const valorNosso = c.valor_total * (c.pct_nosso/100);
                        if (editandoCobrancaId === c.id) {
                          return (
                            <div key={c.id} style={{ display:'flex', flexDirection:'column', gap:8, padding:'12px 14px', background:'#F5EEFF', borderRadius:10, border:'1px solid rgba(107,33,168,0.2)' }}>
                              <input value={editCobranca.descricao} onChange={e => setEditCobranca({...editCobranca, descricao:e.target.value})}
                                placeholder="Descrição" style={{ padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, fontSize:12, fontFamily:'Poppins,sans-serif' }} />
                              <div style={{ display:'flex', gap:8 }}>
                                <input type="number" step="0.01" value={editCobranca.valor_total} onChange={e => setEditCobranca({...editCobranca, valor_total:e.target.value})}
                                  placeholder="Valor" style={{ flex:1, padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, fontSize:12, fontFamily:'Poppins,sans-serif' }} />
                                <input type="date" value={editCobranca.data_prevista} onChange={e => setEditCobranca({...editCobranca, data_prevista:e.target.value})}
                                  style={{ flex:1, padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, fontSize:12, fontFamily:'Poppins,sans-serif' }} />
                              </div>
                              <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                                <button onClick={() => setEditandoCobrancaId(null)}
                                  style={{ background:'white', color:'#7B5EA7', border:'1px solid rgba(107,33,168,0.2)', borderRadius:6, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700 }}>Cancelar</button>
                                <button onClick={() => salvarEdicaoCobranca(c.id)}
                                  style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', borderRadius:6, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700 }}>Salvar</button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={c.id} style={{ display:'grid', gridTemplateColumns:'1fr auto auto', gap:10, alignItems:'center', padding:'10px 14px', background: c.status==='Recebido'?'#f0fdf4':'#fffbeb', borderRadius:10, border:`1px solid ${c.status==='Recebido'?'#bbf7d0':'#fde68a'}` }}>
                            <div>
                              <div style={{ fontSize:12, fontWeight:600, color:'#1E0A3C' }}>{c.descricao}</div>
                              <div style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>{new Date(c.data_prevista+'T00:00').toLocaleDateString('pt-BR')} · nosso: {fmtMoeda(valorNosso)}</div>
                            </div>
                            <div style={{ fontSize:14, fontWeight:800, color: c.status==='Recebido'?'#16a34a':'#b45309', fontFamily:'Playfair Display,serif' }}>{fmtMoeda(c.valor_total)}</div>
                            <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end', maxWidth:120 }}>
                              {c.status!=='Recebido' && (
                                <button onClick={() => marcarCobrancaRecebida(c)}
                                  style={{ background:'#16a34a', color:'white', border:'none', borderRadius:6, padding:'5px 10px', fontSize:10, cursor:'pointer', fontWeight:700 }}>✓ Recebido</button>
                              )}
                              {!c.lancado_financeiro && (
                                <button onClick={() => lancarNoFinanceiro(c)}
                                  style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', borderRadius:6, padding:'5px 10px', fontSize:10, cursor:'pointer', fontWeight:700 }}>→ Financeiro</button>
                              )}
                              {c.lancado_financeiro && <span style={{ fontSize:10, color:'#059669', fontWeight:700 }}>✓ lançado</span>}
                              <button onClick={() => iniciarEdicaoCobranca(c)} title="Editar"
                                style={{ background:'white', color:'#7B5EA7', border:'1px solid rgba(107,33,168,0.2)', borderRadius:6, padding:'5px 9px', fontSize:10, cursor:'pointer', fontWeight:700 }}>✏️</button>
                              <button onClick={() => excluirCobranca(c)} title="Excluir"
                                style={{ background:'white', color:'#dc2626', border:'1px solid #fecaca', borderRadius:6, padding:'5px 9px', fontSize:10, cursor:'pointer', fontWeight:700 }}>🗑</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Aba Notas ── */}
            {abaPainel === 'notas' && (
              <div className="card" style={{ padding:16 }}>
                {(casoAberto.notas||[]).length === 0 && (
                  <div style={{ textAlign:'center', padding:24, color:'#9ca3af', fontSize:12 }}>Nenhuma nota ainda.</div>
                )}
                {(casoAberto.notas||[]).slice().reverse().map((n,i) => (
                  <div key={i} style={{ padding:'10px 12px', background:'#FAF8FF', borderRadius:10, marginBottom:8, borderLeft:'3px solid rgba(107,33,168,0.2)' }}>
                    <div style={{ fontSize:13, color:'#1E0A3C', lineHeight:1.5 }}>{n.texto}</div>
                    <div style={{ fontSize:10, color:'#9ca3af', marginTop:4 }}>{n.data_hora}</div>
                  </div>
                ))}
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <input placeholder="Adicionar nota..." value={novaNota} onChange={e => setNovaNota(e.target.value)}
                    onKeyDown={e => e.key==='Enter' && adicionarNota(casoAberto)}
                    style={{ flex:1, padding:'9px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:9, fontSize:13, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
                  <button onClick={() => adicionarNota(casoAberto)}
                    style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 16px', borderRadius:9, cursor:'pointer', fontSize:14, fontWeight:700 }}>+</button>
                </div>
              </div>
            )}

            {/* ── Aba Documentos ── */}
            {abaPainel === 'documentos' && (
              <div className="card" style={{ padding:16 }}>
                <div style={{ fontSize:11.5, color:'#7B5EA7', marginBottom:14, lineHeight:1.6 }}>
                  Isso é o que o cliente vê na aba "Documentos" do link dele — só leitura pra ele. Clique no status pra ciclar pendente → enviado → assinado.
                </div>
                {documentos.length === 0 && (
                  <div style={{ textAlign:'center', padding:16, color:'#9ca3af', fontSize:12 }}>
                    Nenhum documento na lista ainda.
                    <div style={{ marginTop:10 }}>
                      <button onClick={() => usarChecklistPadrao(casoAberto)}
                        style={{ background:'#F3EBFF', color:'#7B2FBE', border:'1px solid rgba(107,33,168,0.2)', padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600 }}>
                        + usar checklist padrão
                      </button>
                    </div>
                  </div>
                )}
                {documentos.map(d => {
                  const s = STATUS_DOC_INFO[d.status] || STATUS_DOC_INFO.pendente;
                  return (
                    <div key={d.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid rgba(107,33,168,0.05)' }}>
                      <span style={{ fontSize:13, color:'#1E0A3C', flex:1 }}>{d.nome}</span>
                      <button onClick={() => ciclarStatusDocumento(d)}
                        style={{ background:s.bg, color:s.cor, border:'none', padding:'4px 12px', borderRadius:20, fontSize:10.5, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                        {s.label}
                      </button>
                      <button onClick={() => removerDocumento(d)} title="Remover"
                        style={{ background:'white', color:'#dc2626', border:'1px solid #fecaca', borderRadius:6, padding:'4px 8px', fontSize:10, cursor:'pointer' }}>🗑</button>
                    </div>
                  );
                })}
                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <input placeholder="Adicionar documento..." value={novoDocumento} onChange={e => setNovoDocumento(e.target.value)}
                    onKeyDown={e => e.key==='Enter' && adicionarDocumento(casoAberto)}
                    style={{ flex:1, padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
                  <button onClick={() => adicionarDocumento(casoAberto)}
                    style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700 }}>+</button>
                </div>
                {documentos.length > 0 && (
                  <button onClick={() => usarChecklistPadrao(casoAberto)}
                    style={{ marginTop:10, background:'transparent', color:'#7B5EA7', border:'none', cursor:'pointer', fontSize:11, textDecoration:'underline' }}>
                    + acrescentar itens do checklist padrão
                  </button>
                )}

                <div style={{ height:1, background:'rgba(107,33,168,0.08)', margin:'20px 0' }} />

                <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', marginBottom:6, textTransform:'uppercase' }}>Arquivos pro cliente ver</div>
                <div style={{ fontSize:11.5, color:'#7B5EA7', marginBottom:12, lineHeight:1.6 }}>
                  O contrato assinado e comprovantes do que já foi feito — o cliente abre e baixa direto do link dele, na aba "Meus Arquivos", sem precisar pedir.
                </div>

                {erroArquivo && <div style={{ background:'#fee2e2', color:'#dc2626', padding:'8px 12px', borderRadius:8, fontSize:11.5, marginBottom:10 }}>{erroArquivo}</div>}

                {arquivos.length === 0 && <div style={{ textAlign:'center', padding:14, color:'#9ca3af', fontSize:12 }}>Nenhum arquivo enviado ainda.</div>}
                {arquivos.map(a => {
                  const info = CATEGORIA_ARQUIVO_INFO[a.categoria] || CATEGORIA_ARQUIVO_INFO.comprovante;
                  return (
                    <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid rgba(107,33,168,0.05)' }}>
                      <span style={{ fontSize:15 }}>{info.icone}</span>
                      <span onClick={() => handleAbrirArquivo(a)} style={{ fontSize:13, color:'#4A90D9', flex:1, cursor:'pointer', textDecoration:'underline' }}>{a.nome}</span>
                      <span style={{ fontSize:10, color:'#9ca3af' }}>{info.label}</span>
                      <button onClick={() => handleRemoverArquivo(a)} title="Remover"
                        style={{ background:'white', color:'#dc2626', border:'1px solid #fecaca', borderRadius:6, padding:'4px 8px', fontSize:10, cursor:'pointer' }}>🗑</button>
                    </div>
                  );
                })}

                <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap', alignItems:'center' }}>
                  <select value={categoriaNovoArquivo} onChange={e => setCategoriaNovoArquivo(e.target.value)}
                    style={{ padding:'8px 10px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, fontSize:12, background:'#FAF8FF', color:'#1E0A3C' }}>
                    <option value="contrato">📄 Contrato assinado</option>
                    <option value="comprovante">🧾 Comprovante</option>
                  </select>
                  <input placeholder="Nome do arquivo (opcional)" value={nomeNovoArquivo} onChange={e => setNomeNovoArquivo(e.target.value)}
                    style={{ flex:1, minWidth:160, padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
                  <label style={{ background: enviandoArquivo ? '#c9b3e0' : 'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'8px 14px', borderRadius:8, cursor: enviandoArquivo?'default':'pointer', fontSize:12, fontWeight:700 }}>
                    {enviandoArquivo ? 'Enviando...' : '⬆ Enviar arquivo'}
                    <input type="file" disabled={enviandoArquivo} style={{ display:'none' }}
                      onChange={e => { const f = e.target.files?.[0]; e.target.value=''; if (f) handleUploadArquivo(casoAberto, f); }} />
                  </label>
                </div>
              </div>
            )}

            {/* ── Aba Atualizações ── */}
            {abaPainel === 'atualizacoes' && (
              <div className="card" style={{ padding:16 }}>
                <div style={{ fontSize:11.5, color:'#7B5EA7', marginBottom:14, lineHeight:1.6 }}>
                  Isso é o que o cliente vê na aba "Atualizações" do link dele. Combinado: a cada 15 dias depois de protocolar (ou de iniciar as tratativas), postem uma nota aqui — mesmo sem novidade, pra ele não ficar perguntando.
                </div>

                {(() => {
                  const ultima = atualizacoes[0];
                  const dias = ultima ? diasDesde(ultima.created_at) : (casoAberto.dt_acao_protocolada ? diasDesde(casoAberto.dt_acao_protocolada + 'T00:00:00') : null);
                  if (dias === null) return null;
                  const atrasada = dias >= 15;
                  return (
                    <div style={{ background: atrasada ? '#fee2e2' : '#F3EBFF', color: atrasada ? '#dc2626' : '#7B2FBE', borderRadius:8, padding:'8px 12px', fontSize:11.5, fontWeight:600, marginBottom:14 }}>
                      {atrasada ? '⏰ ' : '✓ '}
                      {ultima ? `Última atualização há ${dias} dia${dias>1?'s':''}` : `${dias} dia${dias>1?'s':''} desde a ação protocolada, sem nenhuma atualização ainda`}
                      {atrasada ? ' — hora de postar uma nova.' : ''}
                    </div>
                  );
                })()}

                {atualizacoes.length === 0 && <div style={{ textAlign:'center', padding:16, color:'#9ca3af', fontSize:12 }}>Nenhuma atualização postada ainda.</div>}
                {atualizacoes.map(a => (
                  <div key={a.id} style={{ display:'flex', gap:10, padding:'10px 0', borderBottom:'1px solid rgba(107,33,168,0.05)' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10.5, color:'#9ca3af', fontWeight:600, marginBottom:3 }}>{fmtDataHoraIso(a.created_at)}</div>
                      <div style={{ fontSize:13, color:'#1E0A3C', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{a.texto}</div>
                    </div>
                    <button onClick={() => removerAtualizacao(a)} title="Remover"
                      style={{ background:'white', color:'#dc2626', border:'1px solid #fecaca', borderRadius:6, padding:'4px 8px', fontSize:10, cursor:'pointer', height:24, flexShrink:0 }}>🗑</button>
                  </div>
                ))}

                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <textarea placeholder="Ex: Ainda aguardando retorno do banco. Seguimos acompanhando ativamente." value={novaAtualizacaoTexto} onChange={e => setNovaAtualizacaoTexto(e.target.value)}
                    rows={2}
                    style={{ flex:1, padding:'9px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:9, fontSize:13, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif', resize:'vertical' }} />
                  <button onClick={() => adicionarAtualizacao(casoAberto)} disabled={enviandoAtualizacao}
                    style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 16px', borderRadius:9, cursor:'pointer', fontSize:14, fontWeight:700, alignSelf:'flex-start' }}>
                    {enviandoAtualizacao ? '...' : '+'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Aba Peças ── */}
            {abaPainel === 'pecas' && (
              <div className="card" style={{ padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, gap:10, flexWrap:'wrap' }}>
                  <div style={{ fontSize:11.5, color:'#7B5EA7', lineHeight:1.6 }}>
                    Notificações, petições, embargos e recursos gerados pra este caso na geradora de Peças Jurídicas.
                  </div>
                  <a href={`https://revisao-contratual-bancaria-production.up.railway.app/pecas?casoId=${casoAberto.id}`} target="_blank" rel="noreferrer"
                    style={{ padding:'7px 14px', borderRadius:9, border:'1.5px solid rgba(107,33,168,0.2)', background:'white', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, textDecoration:'none', whiteSpace:'nowrap' }}>
                    ⚖ Abrir geradora
                  </a>
                </div>
                {pecas.length === 0 && <div style={{ textAlign:'center', padding:16, color:'#9ca3af', fontSize:12 }}>Nenhuma peça gerada ainda pra este caso.</div>}
                {pecas.map(p => {
                  const s = STATUS_PECA_INFO[p.status] || STATUS_PECA_INFO.rascunho;
                  return (
                    <a key={p.id} href={`https://revisao-contratual-bancaria-production.up.railway.app/pecas?casoId=${casoAberto.id}&pecaId=${p.id}`} target="_blank" rel="noreferrer"
                      style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid rgba(107,33,168,0.05)', textDecoration:'none' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, color:'#1E0A3C', fontWeight:600 }}>{p.titulo}</div>
                        <div style={{ fontSize:10.5, color:'#9ca3af', marginTop:2 }}>{TITULOS_TIPO_PECA[p.tipo] || p.tipo} · {new Date(p.created_at).toLocaleDateString('pt-BR')}</div>
                      </div>
                      <span style={{ background:s.bg, color:s.cor, padding:'3px 11px', borderRadius:20, fontSize:10.5, fontWeight:700, whiteSpace:'nowrap' }}>{s.label}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal novo/editar caso ── */}
      {modalCaso && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
          <div style={{ background:'white', borderRadius:18, width:540, padding:32, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <h3 style={{ margin:0, color:'#3F0051', fontFamily:'Playfair Display,serif', fontSize:18 }}>{casoEditando?'Editar Caso':'Novo Caso'}</h3>
              <button onClick={() => setModalCaso(false)} style={{ background:'transparent', border:'none', fontSize:22, cursor:'pointer', color:'#9ca3af', lineHeight:1 }}>×</button>
            </div>

            {erroSalvar && (
              <div style={{ background:'#fee2e2', border:'1px solid #dc2626', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#dc2626', fontWeight:600 }}>
                ⚠️ {erroSalvar}
              </div>
            )}

            {[
              { label:'Projeto', key:'projeto_id', type:'select', opts: projetos.map(p=>({val:p.id,label:p.nome})) },
            ].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <select value={form.projeto_id||''} onChange={e => setForm({...form, projeto_id:e.target.value})} style={{ ...inp }}>
                  <option value=''>Selecione...</option>
                  {projetos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
            ))}

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Nome do cliente *</label>
              <input value={form.nome_cliente} onChange={e => setForm({...form, nome_cliente:e.target.value})} style={{ ...inp }} placeholder="Nome completo do cliente" />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:8, textTransform:'uppercase' }}>Tipo(s) de ação</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {tiposAcaoDisponiveis.map(t => {
                  const ativo = (form.tipos_acao||[]).includes(t);
                  return (
                    <span key={t} onClick={() => toggleTipoAcao(t)}
                      style={{ padding:'5px 12px', borderRadius:20, border:`1.5px solid ${ativo?'#7B2FBE':'rgba(107,33,168,0.2)'}`, background:ativo?'linear-gradient(135deg,#3F0051,#7B2FBE)':'white', color:ativo?'white':'#7B5EA7', cursor:'pointer', fontSize:11, fontWeight:ativo?700:400, transition:'all 0.15s' }}>
                      {t}
                    </span>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Responsável</label>
              <select value={form.responsavel||''} onChange={e => setForm({...form, responsavel:e.target.value})} style={{ ...inp }}>
                <option value=''>Selecione...</option>
                {colaboradores.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Link do Drive</label>
              <input value={form.link_drive||''} onChange={e => setForm({...form, link_drive:e.target.value})} style={{ ...inp }} placeholder="https://drive.google.com/..." />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Resumo do caso</label>
              <textarea value={form.resumo||''} onChange={e => setForm({...form, resumo:e.target.value})} rows={3}
                style={{ ...inp, resize:'vertical' }} placeholder="Descreva brevemente o caso..." />
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:8, textTransform:'uppercase' }}>Parceria</label>
              <div style={{ display:'flex', gap:16, marginBottom:8 }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'#1E0A3C' }}>
                  <input type="radio" checked={!form.parceria} onChange={() => setForm({...form, parceria:false, parceiro_nome:''})} style={{ accentColor:'#7B2FBE' }} /> Não
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'#1E0A3C' }}>
                  <input type="radio" checked={!!form.parceria} onChange={() => setForm({...form, parceria:true})} style={{ accentColor:'#7B2FBE' }} /> Sim
                </label>
              </div>
              {form.parceria && (
                <input placeholder="Nome do parceiro" value={form.parceiro_nome||''} onChange={e => setForm({...form, parceiro_nome:e.target.value})} style={{ ...inp }} />
              )}
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={salvarCaso} disabled={salvando}
                style={{ flex:1, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'12px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)', opacity:salvando?0.7:1 }}>
                {salvando ? 'Salvando...' : 'Salvar caso'}
              </button>
              <button onClick={() => setModalCaso(false)}
                style={{ flex:1, background:'transparent', border:'1.5px solid rgba(107,33,168,0.2)', padding:'12px', borderRadius:10, cursor:'pointer', color:'#7B5EA7', fontSize:14, fontFamily:'Poppins,sans-serif', fontWeight:600 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal gerar cobrança ── */}
      {modalCobranca && casoAberto && (() => {
        const forma = FORMAS_PAG.find(f => f.val === formCobranca.forma_pagamento);
        const datas = gerarDatasCobranca();
        const valorNosso = datas.length > 0 ? datas[0].valor * (formCobranca.pct_nosso/100) : 0;
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
            <div style={{ background:'white', borderRadius:20, width:500, padding:32, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}>

              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                  <h3 style={{ margin:'0 0 4px', color:'#3F0051', fontFamily:'Playfair Display,serif', fontSize:19 }}>Gerar Cobrança</h3>
                  <div style={{ fontSize:12, color:'#7B5EA7' }}>{casoAberto.nome_cliente} · {nomeProjeto(casoAberto.projeto_id)}</div>
                </div>
                <button onClick={() => setModalCobranca(false)} style={{ background:'transparent', border:'none', fontSize:22, cursor:'pointer', color:'#9ca3af', lineHeight:1 }}>×</button>
              </div>

              {/* Valor */}
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:6, textTransform:'uppercase' }}>Valor total (R$)</label>
                <input type="number" value={formCobranca.valor_total} placeholder="Ex: 5000"
                  onChange={e => setFormCobranca({...formCobranca, valor_total: e.target.value})}
                  style={{ ...inp, fontSize:16, fontWeight:700 }} />
              </div>

              {/* Forma de pagamento */}
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:8, textTransform:'uppercase' }}>Forma de pagamento</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  {FORMAS_PAG.map(f => (
                    <div key={f.val} onClick={() => setFormCobranca({...formCobranca, forma_pagamento: f.val, n_parcelas: f.parcelas ? formCobranca.n_parcelas : 1})}
                      style={{ border:`2px solid ${formCobranca.forma_pagamento===f.val?'#7B2FBE':'rgba(107,33,168,0.15)'}`, borderRadius:10, padding:'10px 8px', textAlign:'center', cursor:'pointer', background: formCobranca.forma_pagamento===f.val?'#F3EBFF':'white', transition:'all 0.15s' }}>
                      <div style={{ fontSize:18, marginBottom:4 }}>{f.icone}</div>
                      <div style={{ fontSize:10, fontWeight:700, color: formCobranca.forma_pagamento===f.val?'#7B2FBE':'#4B5563' }}>{f.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Parcelas (só se forma suportar) */}
              {forma?.parcelas && (
                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:6, textTransform:'uppercase' }}>Número de parcelas</label>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {[1,2,3,4,5,6,8,10,12].map(n => (
                      <button key={n} onClick={() => setFormCobranca({...formCobranca, n_parcelas: n})}
                        style={{ padding:'7px 14px', border:`2px solid ${formCobranca.n_parcelas===n?'#7B2FBE':'rgba(107,33,168,0.15)'}`, borderRadius:8, background: formCobranca.n_parcelas===n?'#7B2FBE':'white', color: formCobranca.n_parcelas===n?'white':'#4B5563', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                        {n}×
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Data da primeira cobrança */}
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:6, textTransform:'uppercase' }}>
                  {forma?.parcelas ? 'Data da 1ª parcela' : 'Data de vencimento'}
                </label>
                <input type="date" value={formCobranca.data_primeira}
                  onChange={e => setFormCobranca({...formCobranca, data_primeira: e.target.value})}
                  style={{ ...inp }} />
              </div>

              {/* % nosso */}
              <div style={{ marginBottom:20 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:6, textTransform:'uppercase' }}>% da empresa (padrão 30%)</label>
                <input type="number" value={formCobranca.pct_nosso} min="0" max="100"
                  onChange={e => setFormCobranca({...formCobranca, pct_nosso: e.target.value})}
                  style={{ ...inp }} />
              </div>

              {/* Preview das datas */}
              {datas.length > 0 && (
                <div style={{ background:'#FAF8FF', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:12, padding:14, marginBottom:20 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', marginBottom:10, textTransform:'uppercase' }}>
                    {datas.length > 1 ? `${datas.length} cobranças que serão geradas no calendário` : 'Cobrança que será gerada no calendário'}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:180, overflowY:'auto' }}>
                    {datas.map((d, i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 10px', background:'white', borderRadius:8, border:'1px solid rgba(107,33,168,0.08)' }}>
                        <div style={{ fontSize:12, color:'#1E0A3C', fontWeight:600 }}>
                          {datas.length > 1 ? `Parcela ${d.parcela}/${d.total}` : forma?.label}
                        </div>
                        <div style={{ fontSize:12, color:'#7B5EA7' }}>{new Date(d.data+'T00:00').toLocaleDateString('pt-BR')}</div>
                        <div style={{ fontSize:13, fontWeight:700, color:'#3F0051' }}>{fmtMoeda(d.valor)}</div>
                      </div>
                    ))}
                  </div>
                  {datas.length > 0 && (
                    <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid rgba(107,33,168,0.08)', display:'flex', justifyContent:'space-between', fontSize:11, color:'#7B5EA7' }}>
                      <span>Total: <strong style={{ color:'#3F0051' }}>{fmtMoeda(Number(formCobranca.valor_total))}</strong></span>
                      <span>Nosso ({formCobranca.pct_nosso}%): <strong style={{ color:'#16a34a' }}>{fmtMoeda(Number(formCobranca.valor_total) * (formCobranca.pct_nosso/100))}</strong></span>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:'flex', gap:10 }}>
                <button onClick={salvarCobranca} disabled={datas.length===0}
                  style={{ flex:1, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'13px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)', opacity:datas.length===0?0.5:1 }}>
                  Gerar {datas.length > 1 ? `${datas.length} cobranças` : 'cobrança'}
                </button>
                <button onClick={() => setModalCobranca(false)}
                  style={{ flex:1, background:'transparent', border:'1.5px solid rgba(107,33,168,0.2)', padding:'13px', borderRadius:10, cursor:'pointer', color:'#7B5EA7', fontSize:14, fontFamily:'Poppins,sans-serif', fontWeight:600 }}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Confirmação excluir caso */}
      {confirmDeleteCaso && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div className="card" style={{ width:380, padding:28, textAlign:'center' }}>
            <div style={{ fontSize:36, marginBottom:12 }}>🗑</div>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:'var(--text-primary)' }}>Excluir caso?</div>
            <div style={{ fontSize:13, color:'var(--gray-400)', marginBottom:6 }}>
              <strong>{confirmDeleteCaso.nome_cliente}</strong>
            </div>
            <div style={{ fontSize:12, color:'#dc2626', background:'#fee2e2', borderRadius:8, padding:'10px 14px', marginBottom:20 }}>
              ⚠️ Isso também remove todas as cobranças vinculadas e os lançamentos do Financeiro.
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => excluirCaso(confirmDeleteCaso)}
                style={{ flex:1, background:'#dc2626', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>
                Sim, excluir
              </button>
              <button onClick={() => setConfirmDeleteCaso(null)}
                style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
